import { useEffect, useState } from 'react';

const LIMIT = 15;

/**
 * Sélection du document à faire signer par un client présent (rendez-vous,
 * salon), depuis l'entrée « Clients » de la barre latérale.
 *
 * Ces deux pages — signature du devis et signature du contrat — ne
 * s'ouvraient jusqu'ici que par le jeton reçu par email : sans jeton, elles
 * affichaient « Aucun devis sélectionné ». Ce sélecteur leur donne l'entrée
 * qui manquait : on cherche le document, on l'ouvre, on tend l'écran au
 * client. Aucun email n'est nécessaire.
 *
 * Le composant ne connaît ni les devis ni les contrats : `charger` fait la
 * requête, `colonnes` décrit le tableau et `onChoisir` ouvre le document.
 * Ce qui est commun — recherche différée, période, tri, pagination, états —
 * vit ici, une seule fois.
 */
function SelecteurSignature({
  charger,
  colonnes,
  placeholder,
  compter,
  vide,
  estSigne,
  onChoisir,
  libelleAction = 'Faire signer',
  // Court : la colonne Actions est étroite, et l'état « Signé » se lit déjà
  // dans la colonne Signature — le bouton n'a pas à le répéter.
  libelleActionSigne = 'Revoir',
}) {
  const [lignes, setLignes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [saisie, setSaisie] = useState('');
  const [recherche, setRecherche] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('DESC');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Ouverture d'une ligne : le serveur pose le jeton de signature avant que
  // la page ne change, l'attente doit se voir sur la ligne cliquée.
  const [ouvertureId, setOuvertureId] = useState(null);
  const [ligneErreur, setLigneErreur] = useState(null);

  // La recherche part au serveur : on attend une pause de frappe pour ne pas
  // déclencher une requête par caractère (cf. page Devis).
  useEffect(() => {
    const timer = setTimeout(() => {
      setRecherche(saisie);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [saisie]);

  useEffect(() => {
    let annule = false;

    setLoading(true);
    setError(null);

    charger({ page, limit: LIMIT, search: recherche, dateDebut, dateFin, sortBy, sortDir })
      .then((res) => {
        if (annule) return;
        setLignes(res.data || []);
        setTotal(res.pagination?.total ?? (res.data || []).length);
      })
      .catch((err) => {
        if (!annule) setError(err.message || 'Chargement impossible.');
      })
      .finally(() => {
        if (!annule) setLoading(false);
      });

    return () => {
      annule = true;
    };
    // `charger` est une fonction stable du module de service : l'exclure des
    // dépendances évite de relancer la requête à chaque rendu du parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, recherche, dateDebut, dateFin, sortBy, sortDir]);

  function trier(cle) {
    if (!cle) return;
    if (cle === sortBy) {
      setSortDir((d) => (d === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(cle);
      setSortDir('DESC');
    }
    setPage(1);
  }

  /** Chaque changement de filtre ramène à la première page. */
  const filtrer = (setter) => (e) => {
    setter(e.target.value);
    setPage(1);
  };

  async function ouvrir(item) {
    setOuvertureId(item.id);
    setLigneErreur(null);
    try {
      await onChoisir(item);
    } catch (err) {
      setLigneErreur({ id: item.id, message: err.message || 'Ouverture impossible.' });
    } finally {
      setOuvertureId(null);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const debut = total === 0 ? 0 : (page - 1) * LIMIT + 1;
  const fin = Math.min(page * LIMIT, total);
  const filtreActif = Boolean(dateDebut || dateFin || recherche.trim());

  return (
    <div className="signature-liste">
      <div className="devis-toolbar">
        <input
          type="text"
          className="devis-search"
          placeholder={placeholder}
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
        />

        <div className="devis-date-filter">
          <div className="devis-periode">
            <label className="devis-date-field">
              <span>Du</span>
              <input
                type="date"
                value={dateDebut}
                onChange={filtrer(setDateDebut)}
                max={dateFin || undefined}
              />
            </label>
            <label className="devis-date-field">
              <span>Au</span>
              <input
                type="date"
                value={dateFin}
                onChange={filtrer(setDateFin)}
                min={dateDebut || undefined}
              />
            </label>
          </div>
          {filtreActif && (
            <button
              type="button"
              className="devis-date-clear"
              onClick={() => {
                setSaisie('');
                setDateDebut('');
                setDateFin('');
                setPage(1);
              }}
            >
              Réinitialiser
            </button>
          )}
        </div>
        <span className="devis-total">{compter(total)}</span>
      </div>

      <div className="devis-card">
        <div className="devis-table-wrap">
          {loading && <div className="devis-state">Chargement...</div>}

          {!loading && error && <div className="devis-state devis-state-error">{error}</div>}

          {!loading && !error && lignes.length === 0 && (
            <div className="devis-empty">
              <div className="devis-empty-title">{vide.titre}</div>
              <div className="devis-empty-subtitle">{vide.sousTitre}</div>
            </div>
          )}

          {!loading && !error && lignes.length > 0 && (
            <table className="devis-table" data-colonnes={colonnes.length + 1}>
              <thead>
                <tr>
                  {colonnes.map((col) => (
                    <th
                      key={col.cle || col.label}
                      className={col.cle ? 'devis-th-sortable' : undefined}
                      onClick={() => trier(col.cle)}
                    >
                      {col.label}
                      {col.cle && (
                        <span
                          className={`devis-sort-icon${
                            sortBy === col.cle ? ' devis-sort-icon-active' : ''
                          }`}
                        >
                          {sortBy === col.cle ? (sortDir === 'ASC' ? '↑' : '↓') : '↕'}
                        </span>
                      )}
                    </th>
                  ))}
                  <th className="devis-th-actions">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {lignes.map((item) => (
                  <tr key={item.id} className="devis-row" onClick={() => ouvrir(item)}>
                    {colonnes.map((col) => (
                      <td key={col.cle || col.label} data-label={col.label}>
                        {col.rendu(item)}
                      </td>
                    ))}
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className={`devis-btn devis-btn-sm${
                          estSigne(item) ? '' : ' devis-btn-primary'
                        }`}
                        onClick={() => ouvrir(item)}
                        disabled={ouvertureId === item.id}
                      >
                        {ouvertureId === item.id
                          ? 'Ouverture'
                          : estSigne(item)
                            ? libelleActionSigne
                            : libelleAction}
                      </button>
                      {ligneErreur?.id === item.id && (
                        <div className="devis-row-erreur">{ligneErreur.message}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {!loading && !error && total > 0 && (
          <div className="devis-pagination">
            <span className="devis-pagination-info">
              {debut}-{fin} sur {total}
            </span>
            <div className="devis-pagination-controls">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Précédent
              </button>
              <span className="devis-pagination-page">
                Page {page} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SelecteurSignature;
